import { CommunityChannelMessageTextContentTypeEnumItem } from '@ps/api/types';

type ContentType = CommunityChannelMessageTextContentTypeEnumItem;

/**
 * Slate-DOM node shape — local subset of the `Descendant` union that
 * the Slate.js editor in PopshopRN produces. We only need the
 * recursive `children` + leaf `text` access for plain-text flatten,
 * so a hand-rolled type avoids pulling slate.js into the bundle just
 * for rendering.
 */
type SlateNode =
  | { text: string }
  | { type?: string; children?: SlateNode[]; [key: string]: unknown };

/**
 * Flatten a message's stored content to a plain-text string for
 * rendering in slice 1. Three storage formats are possible:
 *
 *   - `plain-text` — return as-is.
 *   - `markdown` — return as-is (the design renders the formatting
 *     literally in the rich-text slice; for now plain readout is
 *     close enough since markdown reads as plain prose with some
 *     punctuation noise).
 *   - `slatejs-dom` — parse the JSON node tree and concatenate every
 *     leaf `text`, joining sibling paragraphs with newlines. Matches
 *     the `renderSlateDOMtoPlainText` shape PopshopRN uses for
 *     channel-list previews / notification text.
 *
 * Malformed Slate JSON falls back to the raw string so the user
 * sees *something* rather than an empty bubble.
 */
export function messageContentToPlainText(
  content: string,
  type: ContentType,
): string {
  if (
    type === CommunityChannelMessageTextContentTypeEnumItem.PlainText ||
    type === CommunityChannelMessageTextContentTypeEnumItem.Markdown
  ) {
    return content;
  }
  if (type === CommunityChannelMessageTextContentTypeEnumItem.SlatejsDom) {
    return slateJsonToPlainText(content);
  }
  return content;
}

function slateJsonToPlainText(json: string): string {
  let nodes: SlateNode[];
  try {
    nodes = JSON.parse(json) as SlateNode[];
  } catch {
    return json;
  }
  if (!Array.isArray(nodes)) return json;
  return nodes
    .map(serializeNode)
    .filter(text => text.length > 0)
    .join('\n')
    .trim();
}

function serializeNode(node: SlateNode): string {
  if (typeof (node as { text?: unknown }).text === 'string') {
    return (node as { text: string }).text;
  }
  const element = node as { type?: string; children?: SlateNode[] };
  // Mentions in PopshopRN's Slate schema carry the rendered
  // `@username` on the node itself (via `profile`); we collapse them
  // by walking children — child[0] is typically a `{ text: '' }`
  // sentinel and the displayable handle lives elsewhere. The
  // serialized form ends up as whatever the children contain, which
  // is good enough for plain-text preview until the rich renderer
  // takes over.
  if (Array.isArray(element.children)) {
    return element.children.map(serializeNode).join('');
  }
  return '';
}
